import { useContext, useMemo, useState } from 'react';
import {
    Card,
    Grid,
    Paper,
    Table,
    TableBody,
    TableContainer,
    TableRow,
    Typography
} from '@mui/material';
import { hoursOrMinutes, useFormatting } from '@/utils/formatting';
import { useTranslation } from 'react-i18next';
import { ShiftStatusWithdrawAllowed } from '@instapaid/shared-logic';
import { UserContext } from '@/contexts/user/UserContext';
import {
    TableCell,
    TableCellFirstCell,
    TableHeader,
    TableHeaderCell,
    TableHeaderCellFirstCell
} from '@/components/shared/styles/Table';
import Chip, { chipColors } from '@/components/shared/Chip';
import { AvailableShift, Tip } from '@/services/PaymentsServiceV2';
import CardHeader from '@/components/shared/CardHeader';
import { SMBold } from '@/typography/Typography';
import { Status } from '@/pages/users/UserTransactions';

export type TipsTableProps = {
    userId: string;
    payrollPeriod: string;
    tips?: Tip[];
    shiftStatusWithdrawAllowed?: ShiftStatusWithdrawAllowed;
    payoutType: string;
    companyName: string;
    heading?: string;
    total?: number;
    totalHours?: number;
};

export const TipsTable = ({
    tips,
    total,
    totalHours,
    shiftStatusWithdrawAllowed,
    payoutType,
    heading,
    companyName
}: TipsTableProps) => {
    const { company: adminCompany } = useContext(UserContext);
    const { getBalanceLabel, toDecimal } = useFormatting();

    const { t } = useTranslation();
    const { formatDate, formatDateTimeToTime } = useFormatting();

    return (
        <>
            {tips && tips?.length > 0 ? (
                <Card>
                    <CardHeader
                        label={heading}
                        description={`${companyName} - ${t('payout_type', {
                            payoutType
                        })}`}
                        chip={
                            <Chip
                                icon={<span></span>}
                                label={
                                    <SMBold
                                        color={chipColors.success.text}
                                    >{`${tips.length}`}</SMBold>
                                }
                                color={'success'}
                            />
                        }
                    />
                    <Grid md={12}></Grid>

                    <TableContainer component={Paper}>
                        <Table aria-label="tips table">
                            <TableHeader>
                                <TableRow>
                                    <TableHeaderCellFirstCell>
                                        Date
                                    </TableHeaderCellFirstCell>
                                    <TableHeaderCellFirstCell>
                                        {t('distributed_at')}
                                    </TableHeaderCellFirstCell>
                                    <TableHeaderCell>Time</TableHeaderCell>
                                    <TableHeaderCell>Pause</TableHeaderCell>
                                    <TableHeaderCell>
                                        Worked Hours
                                    </TableHeaderCell>
                                    <TableHeaderCell>Status</TableHeaderCell>
                                    <TableHeaderCell align="right">
                                        Tips earned
                                    </TableHeaderCell>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {tips?.map((row, index) => (
                                    <TableRow key={index}>
                                        <TableCellFirstCell>
                                            {formatDate(
                                                row.startDateTime,
                                                undefined,
                                                true
                                            )}
                                        </TableCellFirstCell>
                                        <TableCellFirstCell>
                                            {formatDate(row.createdAt)}
                                        </TableCellFirstCell>
                                        <TableCell>
                                            {row.startDateTime &&
                                            row.endDateTime ? (
                                                <>
                                                    {formatDateTimeToTime(
                                                        row.startDateTime
                                                    )}{' '}
                                                    -{' '}
                                                    {formatDateTimeToTime(
                                                        row.endDateTime
                                                    )}
                                                </>
                                            ) : (
                                                <>Period not reported</>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {hoursOrMinutes(row.break)}{' '}
                                        </TableCell>

                                        <TableCell style={{ width: '140px' }}>
                                            {toDecimal(row.duration, 1)}
                                        </TableCell>

                                        <TableCell style={{ width: '138px' }}>
                                            {row.status && (
                                                <Status status={row.status} />
                                            )}
                                        </TableCell>

                                        <TableCell
                                            align="right"
                                            // color={!row.availableForPayout}
                                        >
                                            {getBalanceLabel(
                                                row.amount,
                                                adminCompany.currency
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                            <TableHeader>
                                <TableRow>
                                    <TableHeaderCell>
                                        You earned
                                    </TableHeaderCell>
                                    <TableHeaderCell></TableHeaderCell>
                                    <TableHeaderCell></TableHeaderCell>
                                    <TableHeaderCell></TableHeaderCell>
                                    <TableHeaderCell style={{ width: '80px' }}>
                                        {totalHours}
                                    </TableHeaderCell>
                                    <TableHeaderCell></TableHeaderCell>
                                    <TableHeaderCell align="right">
                                        {getBalanceLabel(
                                            total,
                                            adminCompany.currency
                                        )}
                                    </TableHeaderCell>
                                </TableRow>
                            </TableHeader>
                        </Table>
                    </TableContainer>
                </Card>
            ) : (
                <Typography textAlign={'center'}>
                    {t('no_available_tips')}
                </Typography>
            )}
        </>
    );
};
